import { router as baseRouter } from "@foretagsplatsen/widgetjs";
import nullPeriod from "../../../models/period/nullPeriod.js";
import periodPickerWidget from "../../../core/widgets/periodPicker.js";

/**
 * Model holding all the parts of an accounting document. It ensures the communication between all the parts
 * of the document.
 * The coordinator registers to events of all parts, and has the responsibility to forward those events to
 * the correct parts by sending them messages.
 * It also provides accessors so all sub-parts accessing goes through the coordinator.
 *
 * @abstract
 * @param {AccountingDocumentState} documentState - Model holding the document's data and manipulating it
 * @param {object} renderables - Object parameter holding all the renderable parts of the document
 * @param {AccountingActionsWidget} [renderables.actions] - Widget rendering the document actions on the right-hand side of the
 *   content. The action bar is opened/closed by a button located in the header. The `actions` and the `drawer` are
 *   mutually exclusives
 * @param {AccountingDocumentContentWidget} renderables.content - Main part of the document, showing the loaded data
 * @param {Renderable} [renderables.drawer] - Widget rendering contextual information after an user's interaction.
 *   The drawer renders on the right-hand side of the content, and partially covers it. The drawer contains a button
 *   to close it. The `drawer` and the `actions` are mutually exclusives
 * @param {Renderable} renderables.title - Title and subtitle of the document. It also often has a help button
 * @param {Renderable} [renderables.periodPicker] - Picker for the current period on the top of the frame.
 *   In the case where no period picker is wanted (like in archives for example), `null` or `false` can be used
 * @param {Router} [router] - Router handling navigation, like redirections
 */
export default class AccountingCoordinator {
	constructor({
		documentState,
		session,
		renderables: {
			actions,
			content,
			drawer,
			title,
			periodPicker = periodPickerWidget({
				activeYears: session.getActiveYears() || [],
				redirectToPeriod: session.redirectToPeriod.bind(session),
				period: session.getPeriod() || nullPeriod,
			}),
		},
		router = baseRouter.getRouter(),
	}) {
		this._actions = actions;
		this._content = content;
		this._drawer = drawer;
		this._title = title;
		this._periodPicker = periodPicker;
		this._documentState = documentState;
		this._router = router;

		this._registerToPopState();
	}

	async setup(...rest) {
		await this._documentState.setup({
			...rest[0],
			...this._setupDocumentStateCallbacks(),
		});

		this._title?.setup?.({
			documentState: this._documentState,
			...this._setupTitleCallbacks(),
		});

		this._content.setup({
			documentState: this._documentState,
			...this._setupContentCallbacks(),
		});

		this._actions?.setup({
			documentState: this._documentState,
			...this._setupActionsCallbacks(),
		});
	}

	getActions() {
		return this._actions;
	}

	getDrawer() {
		return this._drawer;
	}

	getContent() {
		return this._content;
	}

	getTitle() {
		return this._title;
	}

	getPeriodPicker() {
		return this._periodPicker;
	}

	hasPeriodPicker() {
		return !!this._periodPicker;
	}

	hasDrawer() {
		return !!this._drawer;
	}

	hasActions() {
		return !!this._actions;
	}

	wantsActions() {
		return false;
	}

	shouldNotifyAlerts() {
		return true;
	}

	/**
	 * Override to return true if the document shouldn't overflow the
	 * viewport, even with large headers on the page. This is useful
	 * for documents having an inner scrollbar, and to avoid having
	 * two scrollbars on the page.
	 */
	shouldDocumentFitViewport() {
		return false;
	}

	/**
	 * Method invoked when a current period changes in the url.
	 * The application call this method to allow the document to react to this.
	 *
	 * @param {function} fallback - When the period changed is not handled, the document can execute this fallback to
	 * give the hand back to the application for further dispatch.
	 * @param {Period} period - Newly selected period
	 */
	onCurrentPeriodChanged({ fallback: _fallback, period: _period }) {
		// do nothing by default
	}

	triggerPostShowWork() {}

	handleOnDrawerWidthChange({ isDrawerInIconMode: _isDrawerInIconMode }) {}

	_showDrawerWith(content) {
		this._drawer.setContent(content);
		this._drawer.show();
	}

	_hideDrawer() {
		this._drawer.hide();
	}

	_redirectTo(...args) {
		this._router.redirectTo(...args);
	}

	/**
	 * Modify the current URL and add new parameters.
	 *
	 * @param {object} parameters - All parameters that should be
	 * added
	 */
	_setUrlParameters(parameters) {
		const url = this._router.linkToParameters(parameters);
		history.pushState(parameters, "", url.toString());
	}

	/**
	 * Register to the 'popstate' event that is eventually triggered
	 * when the user click the "back" button from the browser.
	 *
	 * Since we manually push/replace some state in the history session,
	 * we need to handle the 'popstate' event to navigate to the correct
	 * tab as the page is not reloaded, meaning the app.js routing won't
	 * be used.
	 */
	_registerToPopState() {
		window.addEventListener("popstate", (event) => {
			if (event.state === null) {
				// the browser will refresh the page
				return;
			}

			this._onPopState(event.state);
		});
	}

	/**
	 * This method is triggered when the active history entry changes
	 * while the user navigates the session history. It is fired when
	 * the user clicks on the back or forward button (or calling
	 * `history.back()` or `history.forward()`).
	 *
	 * @param {object} state - Copy of the history entry. It contains
	 * the URL parameters
	 */
	_onPopState(_state) {
		// Override to specify a PopState callback behavior
	}

	/**
	 * @return {object} Keys are callback names and values are
	 * functions. The document state declares the used
	 * callback names in its `setup()` function. The functions passed
	 * as values are invoked by the document state.
	 */
	_setupDocumentStateCallbacks() {
		return {};
	}

	/**
	 * @return {object} Keys are callback names and values are
	 * functions. The document's title widget declares the used
	 * callback names in its `setup()` function. The functions passed
	 * as values are invoked by the document's content widget.
	 */
	_setupTitleCallbacks() {
		return {};
	}

	/**
	 * @return {object} Keys are callback names and values are
	 * functions. The document's content widget declares the used
	 * callback names in its `setup()` function. The functions passed
	 * as values are invoked by the document's content widget.
	 */
	_setupContentCallbacks() {
		return {};
	}

	/**
	 * @return {object} Keys are callback names and values are
	 * functions. The document's actions widget declares the used
	 * callback names in its `setup()` function. The functions passed
	 * as values are invoked by the document's actions widget.
	 */
	_setupActionsCallbacks() {
		return {};
	}
}
